// Pure sizing math for the badge template live preview. Kept out of the
// component so it can be unit tested — jsdom's ResizeObserver never reports a
// width, so the fit path is unreachable through the rendered component.

// CR80 landscape badge card size (3.375in x 2.125in) at 96 DPI — matches the
// `@page { size: 3.375in 2.125in }` the templates themselves declare.
export const BADGE_CARD_WIDTH_PX = 3.375 * 96; // 324
export const BADGE_CARD_HEIGHT_PX = 2.125 * 96; // 204

export const MIN_SCALE = 25;
export const MAX_SCALE = 400;
export const VIEWPORT_PADDING_PX = 24; // 12px padding on each side of the Box
export const FALLBACK_SCALE = 100; // when the container has not been measured yet

// Share of the window the preview may claim vertically. Stacked badges are a
// tall shape in a wide column, so height — not width — is what caps the fit;
// the rest is left for the modal header, footer, and validation alerts.
export const HEIGHT_BUDGET_RATIO = 0.65;
const MIN_HEIGHT_BUDGET_PX = 280;
const MAX_HEIGHT_BUDGET_PX = 900;

const BADGE_SECTION_REGEX = /class\s*=\s*["'][^"']*\bbadge\b/gi;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// The iframe is `sandbox=""` (no allow-same-origin), so the parent cannot read
// contentDocument to measure the rendered template. Count sections off the raw
// markup instead.
export function countBadgeSections(html: string | undefined): number {
  if (!html) return 1;
  return Math.max(1, html.match(BADGE_SECTION_REGEX)?.length ?? 1);
}

export function resolveHeightBudget(windowHeight: number): number {
  return clamp(
    windowHeight * HEIGHT_BUDGET_RATIO,
    MIN_HEIGHT_BUDGET_PX,
    MAX_HEIGHT_BUDGET_PX
  );
}

export interface BadgeFitInput {
  /** Content-box width of the preview viewport. 0 when not yet measured. */
  containerWidth: number;
  /** Vertical space the preview may occupy, border-box. */
  heightBudget: number;
  /** Unscaled height of all badge sections stacked. */
  contentHeight: number;
}

// Largest zoom (%) at which the whole template fits both axes of the viewport.
// Width and height budgets are both content-box, so the padding comes off the
// height too — otherwise the scaled content overflows the capped viewport by
// exactly the padding and pins a scrollbar open. Floors rather than rounds:
// rounding up reintroduces that overflow.
export function computeFitScale({
  containerWidth,
  heightBudget,
  contentHeight,
}: BadgeFitInput): number {
  if (containerWidth <= 0 || contentHeight <= 0) return FALLBACK_SCALE;

  const widthFit = (containerWidth / BADGE_CARD_WIDTH_PX) * 100;
  const heightFit = ((heightBudget - VIEWPORT_PADDING_PX) / contentHeight) * 100;

  return Math.floor(clamp(Math.min(widthFit, heightFit), MIN_SCALE, MAX_SCALE));
}
